import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import type { ClientDto, ConnectionsSummary, InvoiceDto, InvoiceInput, InvoiceStatus } from '@jordan/types';
import {
  Alert,
  Breadcrumb,
  Button,
  DataTable,
  type DataTableColumn,
  DatePicker,
  Input,
  Modal,
  PageHeader,
  SelectMenu,
  Spinner,
  Textarea,
} from '@jordan/ui';
import { AppChrome } from '../../components/AppChrome';
import { apiDelete, apiGet, apiPost, apiPut } from '../../lib/api';
import { billingTimeSidebar } from './billingTimeSidebar';

const todayYmd = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const money = (n: number, currency: string) => {
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency: currency || 'USD' }).format(n);
  } catch {
    return `${n.toFixed(2)} ${currency}`;
  }
};
const fmtDate = (ymd: string | null) =>
  ymd ? new Date(`${ymd}T00:00:00`).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : '—';

type Badge = { label: string; variant: 'success' | 'danger' | 'warning' | 'secondary' };
function statusBadge(inv: InvoiceDto): Badge {
  if (inv.status === 'PAID') return { label: 'Paid', variant: 'success' };
  if (inv.status === 'DRAFT') return { label: 'Draft', variant: 'secondary' };
  if (inv.dueDate && inv.dueDate < todayYmd()) return { label: 'Overdue', variant: 'danger' };
  return { label: 'Open', variant: 'warning' };
}

interface LineForm {
  description: string;
  quantity: string;
  unitPrice: string;
}
interface InvoiceForm {
  docNumber: string;
  clientId: string;
  customerName: string;
  txnDate: string;
  dueDate: string;
  currency: string;
  status: InvoiceStatus;
  memo: string;
  note: string;
  billEmail: string;
  lines: LineForm[];
}
const blankLine = (): LineForm => ({ description: '', quantity: '1', unitPrice: '0' });
const emptyForm = (): InvoiceForm => ({
  docNumber: '',
  clientId: '',
  customerName: '',
  txnDate: todayYmd(),
  dueDate: '',
  currency: 'USD',
  status: 'OPEN',
  memo: '',
  note: '',
  billEmail: '',
  lines: [blankLine()],
});
const toForm = (inv: InvoiceDto): InvoiceForm => ({
  docNumber: inv.docNumber,
  clientId: inv.clientId ?? '',
  customerName: inv.customerName,
  txnDate: inv.txnDate,
  dueDate: inv.dueDate ?? '',
  currency: inv.currency,
  status: inv.status,
  memo: inv.memo ?? '',
  note: inv.note ?? '',
  billEmail: inv.billEmail ?? '',
  lines: inv.lines.length
    ? inv.lines.map((l) => ({ description: l.description ?? '', quantity: String(l.quantity), unitPrice: String(l.unitPrice) }))
    : [blankLine()],
});

export function InvoicesPage() {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const [invoices, setInvoices] = useState<InvoiceDto[] | null>(null);
  const [clients, setClients] = useState<ClientDto[]>([]);
  const [qbConnected, setQbConnected] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [editing, setEditing] = useState<InvoiceDto | 'new' | null>(null);
  const [form, setForm] = useState<InvoiceForm>(emptyForm());
  const [busy, setBusy] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const load = () =>
    apiGet<InvoiceDto[]>('/api/consulting/invoices')
      .then(setInvoices)
      .catch(() => setErr('Could not load invoices.'));
  useEffect(() => {
    void load();
    apiGet<ClientDto[]>('/api/consulting/clients').then(setClients).catch(() => undefined);
    apiGet<ConnectionsSummary>('/api/connections')
      .then((c) => setQbConnected(c.quickbooks.accounts.length > 0))
      .catch(() => undefined);
  }, []);

  const openNew = () => {
    setEditing('new');
    setForm(emptyForm());
  };
  const openEdit = (inv: InvoiceDto) => {
    setEditing(inv);
    setForm(toForm(inv));
  };
  const close = () => setEditing(null);
  const set = <K extends keyof InvoiceForm>(key: K, value: InvoiceForm[K]) => setForm((f) => ({ ...f, [key]: value }));
  const setLine = (i: number, key: keyof LineForm, value: string) =>
    setForm((f) => ({ ...f, lines: f.lines.map((l, idx) => (idx === i ? { ...l, [key]: value } : l)) }));
  const addLine = () => setForm((f) => ({ ...f, lines: [...f.lines, blankLine()] }));
  const removeLine = (i: number) => setForm((f) => ({ ...f, lines: f.lines.filter((_, idx) => idx !== i) }));

  const formTotal = form.lines.reduce((s, l) => s + (Number(l.quantity) || 0) * (Number(l.unitPrice) || 0), 0);

  const save = async () => {
    setBusy(true);
    setErr(null);
    const payload: InvoiceInput = {
      docNumber: form.docNumber.trim() || undefined,
      clientId: form.clientId || null,
      customerName: form.customerName.trim() || undefined,
      txnDate: form.txnDate || todayYmd(),
      dueDate: form.dueDate || null,
      currency: form.currency.trim() || 'USD',
      status: form.status,
      memo: form.memo,
      note: form.note,
      billEmail: form.billEmail,
      lines: form.lines
        .filter((l) => l.description.trim() !== '' || Number(l.unitPrice) > 0)
        .map((l) => ({ description: l.description, quantity: Number(l.quantity) || 0, unitPrice: Number(l.unitPrice) || 0 })),
    };
    try {
      if (editing === 'new') await apiPost('/api/consulting/invoices', payload);
      else if (editing) await apiPut(`/api/consulting/invoices/${editing.id}`, payload);
      close();
      await load();
    } catch {
      setErr('Could not save the invoice.');
    } finally {
      setBusy(false);
    }
  };

  const remove = async (inv: InvoiceDto) => {
    if (!window.confirm(`Delete invoice ${inv.docNumber}? This can’t be undone.`)) return;
    try {
      await apiDelete(`/api/consulting/invoices/${inv.id}`);
      await load();
    } catch {
      setErr('Could not delete the invoice.');
    }
  };

  const sync = async () => {
    setSyncing(true);
    setErr(null);
    setNotice(null);
    try {
      const res = await apiPost<{ synced: number; invoices: InvoiceDto[] }>('/api/consulting/invoices/sync');
      setInvoices(res.invoices);
      setNotice(`Synced ${res.synced} invoice${res.synced === 1 ? '' : 's'} from QuickBooks.`);
    } catch {
      setErr('Could not sync from QuickBooks.');
    } finally {
      setSyncing(false);
    }
  };

  const columns: DataTableColumn<InvoiceDto>[] = [
    {
      key: 'docNumber',
      header: 'Invoice #',
      sortable: true,
      render: (i) => (
        <span className="fw-semibold">
          {i.docNumber}
          {i.source === 'quickbooks' && <i className="ph-cloud-check ms-1 text-muted" title="From QuickBooks" />}
        </span>
      ),
    },
    { key: 'customerName', header: 'Customer', sortable: true, render: (i) => i.customerName },
    { key: 'txnDate', header: 'Date', sortable: true, render: (i) => fmtDate(i.txnDate) },
    { key: 'dueDate', header: 'Due', sortable: true, render: (i) => fmtDate(i.dueDate) },
    { key: 'total', header: 'Amount', align: 'end', sortable: true, sortValue: (i) => i.total, render: (i) => money(i.total, i.currency) },
    { key: 'balance', header: 'Balance', align: 'end', sortable: true, sortValue: (i) => i.balance, render: (i) => money(i.balance, i.currency) },
    {
      key: 'status',
      header: 'Status',
      sortValue: (i) => statusBadge(i).label,
      render: (i) => {
        const b = statusBadge(i);
        return <span className={`badge bg-${b.variant} bg-opacity-25 text-${b.variant}`}>{b.label}</span>;
      },
    },
    {
      key: 'actions',
      header: '',
      align: 'end',
      render: (i) => (
        <div className="d-flex justify-content-end gap-1">
          <button className="btn btn-light btn-icon btn-sm rounded-pill border-transparent" title="Edit" onClick={() => openEdit(i)}>
            <i className="ph-pencil" />
          </button>
          <button className="btn btn-light btn-icon btn-sm rounded-pill border-transparent text-danger" title="Delete" onClick={() => void remove(i)}>
            <i className="ph-trash" />
          </button>
        </div>
      ),
    },
  ];

  const clientOptions = [{ value: '', label: 'No client' }, ...clients.map((c) => ({ value: c.id, label: c.name }))];

  return (
    <AppChrome secondarySidebar={billingTimeSidebar(navigate, pathname)}>
      <PageHeader
        title={
          <>
            Billing &amp; Time - <span className="fw-normal">Invoices</span>
          </>
        }
        breadcrumb={
          <Breadcrumb
            items={[
              { icon: 'ph-house', href: '/' },
              { label: 'Consulting' },
              { label: 'Billing & Time' },
              { label: 'Invoices' },
            ]}
          />
        }
        actions={
          <div className="d-flex gap-2">
            {qbConnected && (
              <Button variant="light" icon="ph-cloud-arrow-down" disabled={syncing} onClick={() => void sync()}>
                Sync from QuickBooks
              </Button>
            )}
            <Button variant="primary" icon="ph-plus" onClick={openNew}>
              New invoice
            </Button>
          </div>
        }
      />

      <div className="content">
        {notice && (
          <Alert variant="success" icon="ph-check-circle" dismissible>
            {notice}
          </Alert>
        )}
        {err && (
          <Alert variant="warning" icon="ph-warning" dismissible>
            {err}
          </Alert>
        )}

        {invoices === null ? (
          <Spinner />
        ) : invoices.length === 0 ? (
          <div className="card border-0 shadow-sm">
            <div className="card-body text-center py-5">
              <i className="ph-receipt d-block fs-1 text-muted mb-2" />
              <h5 className="mb-1">No invoices yet</h5>
              <p className="text-muted mb-3">Create an invoice — QuickBooks isn’t required.</p>
              <Button variant="primary" icon="ph-plus" onClick={openNew}>
                New invoice
              </Button>
            </div>
          </div>
        ) : (
          <div className="card border-0 shadow-sm">
            <DataTable
              columns={columns}
              rows={invoices}
              getRowKey={(i) => i.id}
              searchable
              searchPlaceholder="Search invoices…"
              initialSort={{ key: 'txnDate', dir: 'desc' }}
              pageSize={20}
            />
          </div>
        )}
      </div>

      {editing && (
        <Modal
          open
          size="xl"
          onClose={close}
          title={editing === 'new' ? 'New invoice' : `Invoice ${(editing as InvoiceDto).docNumber}`}
          footer={
            <>
              <Button variant="light" onClick={close}>
                Cancel
              </Button>
              <Button variant="primary" disabled={busy} onClick={() => void save()}>
                {editing === 'new' ? 'Create invoice' : 'Save changes'}
              </Button>
            </>
          }
        >
          <div className="row">
            <div className="col-md-4">
              <SelectMenu
                label="Client"
                icon="ph-identification-card"
                value={form.clientId}
                onChange={(v) => {
                  const c = clients.find((x) => x.id === v);
                  setForm((f) => ({ ...f, clientId: v, customerName: c && !f.customerName ? c.name : f.customerName }));
                }}
                options={clientOptions}
              />
            </div>
            <div className="col-md-4">
              <Input label="Customer name" value={form.customerName} onChange={(e) => set('customerName', e.target.value)} />
            </div>
            <div className="col-md-4">
              <Input label="Invoice #" value={form.docNumber} onChange={(e) => set('docNumber', e.target.value)} placeholder="Auto" />
            </div>
            <div className="col-md-3">
              <DatePicker label="Invoice date" value={form.txnDate} onChange={(v) => set('txnDate', v)} />
            </div>
            <div className="col-md-3">
              <DatePicker label="Due date" value={form.dueDate} onChange={(v) => set('dueDate', v)} placeholder="None" />
            </div>
            <div className="col-md-3">
              <SelectMenu
                label="Status"
                icon="ph-flag"
                value={form.status}
                onChange={(v) => set('status', v as InvoiceStatus)}
                options={[
                  { value: 'DRAFT', label: 'Draft' },
                  { value: 'OPEN', label: 'Open' },
                  { value: 'PAID', label: 'Paid' },
                ]}
              />
            </div>
            <div className="col-md-3">
              <Input label="Currency" value={form.currency} onChange={(e) => set('currency', e.target.value)} />
            </div>
          </div>

          {/* Line items */}
          <h6 className="text-muted text-uppercase fs-sm mt-2">Line items</h6>
          <div className="table-responsive">
            <table className="table table-sm align-middle">
              <thead>
                <tr>
                  <th>Description</th>
                  <th className="text-end" style={{ width: 100 }}>Qty</th>
                  <th className="text-end" style={{ width: 130 }}>Unit price</th>
                  <th className="text-end" style={{ width: 130 }}>Amount</th>
                  <th style={{ width: 40 }} />
                </tr>
              </thead>
              <tbody>
                {form.lines.map((l, i) => (
                  <tr key={i}>
                    <td>
                      <Input className="mb-0" value={l.description} onChange={(e) => setLine(i, 'description', e.target.value)} placeholder="Service / item" />
                    </td>
                    <td>
                      <Input className="mb-0 text-end" type="number" min={0} step="0.01" value={l.quantity} onChange={(e) => setLine(i, 'quantity', e.target.value)} />
                    </td>
                    <td>
                      <Input className="mb-0 text-end" type="number" min={0} step="0.01" value={l.unitPrice} onChange={(e) => setLine(i, 'unitPrice', e.target.value)} />
                    </td>
                    <td className="text-end font-monospace">
                      {money((Number(l.quantity) || 0) * (Number(l.unitPrice) || 0), form.currency)}
                    </td>
                    <td className="text-end">
                      <button
                        className="btn btn-light btn-icon btn-sm rounded-pill border-transparent text-danger"
                        title="Remove line"
                        disabled={form.lines.length === 1}
                        onClick={() => removeLine(i)}
                      >
                        <i className="ph-x" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={5}>
                    <Button variant="light" size="sm" icon="ph-plus" onClick={addLine}>
                      Add line
                    </Button>
                  </td>
                </tr>
                <tr className="border-top">
                  <th colSpan={3} className="text-end">Total</th>
                  <th className="text-end font-monospace">{money(formTotal, form.currency)}</th>
                  <th />
                </tr>
              </tfoot>
            </table>
          </div>

          <div className="row">
            <div className="col-md-6">
              <Input label="Billing email" type="email" value={form.billEmail} onChange={(e) => set('billEmail', e.target.value)} />
            </div>
            <div className="col-md-6">
              <Textarea label="Memo (shown on the invoice)" rows={2} value={form.memo} onChange={(e) => set('memo', e.target.value)} />
            </div>
            <div className="col-12">
              <Textarea label="Private note" rows={2} value={form.note} onChange={(e) => set('note', e.target.value)} />
            </div>
          </div>
        </Modal>
      )}
    </AppChrome>
  );
}
